import Dropdown from 'components/dropdown';
import { useContext, useEffect, useRef } from 'react';
import { FiAlignJustify, FiSearch } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import routes from 'routes';
import { io } from 'socket.io-client';
import Configurator from './Configurator';
import commonServices from 'ApiService/Common/commonServices';
import avatar from 'assets/img/avatars/avatar4.png';
import { AuthContext } from 'contexts/AuthContext';
import LogOutConfirmModel from 'pages/Common/LogOutConfirmModel';
import SelectBranchModal from 'pages/Common/SelectBranchModal';
import ProfilePic from 'pages/Student/Profile/ProfilePic/ProfilePic';
import SelcetBranch from 'pages/SuperAdmin/SelcetBranch/SelcetBranch';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { IoMdNotificationsOutline } from 'react-icons/io';
import { RiMoonFill, RiSunFill } from 'react-icons/ri';
import { getNotificationNavigationTarget } from './notficationredirection';

const Navbar = (props) => {
  const { onOpenSidenav, brandText, mini, hovered } = props;
  const navigate = useNavigate();
  const [darkmode, setDarkmode] = useState(() => {
    const storedMode = localStorage.getItem('darkmode');
    return storedMode ? JSON.parse(storedMode) : false;
  });
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const { state, Logout } = useContext(AuthContext);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isBranchModalOpen, setBranchModalOpen] = useState(false);
  const navRef = useRef(null);

  // The navbar is fixed, and on narrow screens it wraps to several rows.
  // Publish where it ends so layouts can pad their content below it.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const update = () => {
      const bottom = Math.ceil(nav.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty(
        '--navbar-bottom',
        `${bottom}px`
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  const userRole = state?.user?.role;
  const branchSelected = state?.selectedBranch;
  //&& userRole !== 'placement'
  useEffect(() => {
    if (userRole) {
      fetchNotifications(userRole);
    }
  }, [userRole]);

  useEffect(() => {
    if (userRole === 'superadmin' && !branchSelected) {
      setBranchModalOpen(true);
    }
  }, [branchSelected, userRole]);

  const fetchNotifications = async () => {
    try {
      const response = await commonServices.getJobNotifications(userRole);

      if (response.data.success) {
        const unreadNotifications = response.data.notifications.filter(
          (notification) => !notification.isRead
        );
        setNotifications(unreadNotifications.slice(0, 5));
        setUnreadCount(unreadNotifications.length);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to fetch notifications');
    }
  };

  useEffect(() => {
    if (!state?.user?.id) return;

    const socketURL = process.env.REACT_APP_API_BASE_URL.replace('/api/v1', '');

    const socket = io(socketURL, {
      withCredentials: true,
      transports: ['websocket'],
    });

    socket.on('connect', () => {
      socket.emit('join', state.user.id);
    });

    socket.on('NotifyUser', async (data) => {
      console.log('🔔 Notification received:', data);
      await fetchNotifications();
    });

    return () => socket.disconnect();
  }, [state?.user?.id]);

  const markAsRead = async (notificationId) => {
    try {
      const response = await commonServices.markNotificationAsRead(
        userRole,
        notificationId
      );

      if (response.data.success) {
        setNotifications((prev) =>
          prev.filter((n) => n._id !== notificationId)
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));
        toast.success('Notification marked as read');
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await commonServices.markAllNotificationsAsRead(
        userRole
      );
      if (response.data.success) {
        setNotifications([]);
        setUnreadCount(0);
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Just now';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Just now';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  useEffect(() => {
    if (state) {
      setIsLoading(false);
    }
  }, [state]);

  useEffect(() => {
    document.body.classList.toggle('dark', darkmode);
    localStorage.setItem('darkmode', JSON.stringify(darkmode));
  }, [darkmode]);

  const filterRoutesByRole = (routes, role) => {
    if (!role) {
      return routes;
    }
    return routes.reduce((acc, route) => {
      if (route.path && route.path.includes(`/${role}`)) {
        const filteredRoute = { ...route };
        if (route.items) {
          filteredRoute.items = route.items.filter(
            (item) =>
              item.path.includes(`/${role}`) || !item.role || item.role === role
          );
        }
        acc.push(filteredRoute);
      }
      return acc;
    }, []);
  };

  // const filteredRoutes = filterRoutesByRole(routes, user.role);
  const filteredRoutes = userRole
    ? filterRoutesByRole(routes, userRole)
    : routes;

  const handleSearch = (input) => {
    setSearchInput(input);
    if (input.length > 0) {
      const filteredResults = filteredRoutes.flatMap((route) =>
        route.items
          ? route.items.flatMap((item) =>
              item.items
                ? item.items.filter((subItem) =>
                    subItem.name.toLowerCase().includes(input.toLowerCase())
                  )
                : item.name.toLowerCase().includes(input.toLowerCase())
                ? [item]
                : []
            )
          : []
      );
      setSearchResults(filteredResults);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectResult = (result) => {
    setSearchInput('');
    setSearchResults([]);
    if (result.layout && result.path) {
      navigate(result.layout + result.path);
    }
  };

  const getProfilePath = (role) => {
    switch (role) {
      case 'admin':
        return '/admin/profile';
      case 'teacher':
        return '/teacher/profile';
      case 'student':
        return '/student/profile';
      default:
        return '/profile'; // Default profile path if role is unknown
    }
  };

  // const profilePath = getProfilePath(userRole);

  const handleLogout = () => {
    setLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    Logout();
    setLogoutModalOpen(false);
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification._id);
    const targetPath = getNotificationNavigationTarget(
      notification.type,
      userRole,
      notification.data,
      state?.user?.hrRole
    );

    navigate(targetPath);
  };

  return (
    <nav
      ref={navRef}
      className={`duration-175 linear fixed right-3 top-3 flex flex-row flex-wrap items-center justify-between rounded-xl bg-white/30 transition-all  ${
        mini === false
          ? 'w-[calc(100vw_-_6%)] md:w-[calc(100vw_-_8%)] lg:w-[calc(100vw_-_6%)] xl:w-[calc(100vw_-_350px)] 2xl:w-[calc(100vw_-_365px)]'
          : !mini || (mini && hovered)
          ? 'w-[calc(100vw_-_6%)] md:w-[calc(100vw_-_8%)] lg:w-[calc(100vw_-_6%)] xl:w-[calc(100vw_-_350px)] 2xl:w-[calc(100vw_-_365px)]'
          : 'w-[calc(100vw_-_6%)] md:w-[calc(100vw_-_8%)] lg:w-[calc(100vw_-_6%)] xl:w-[calc(100vw_-_180px)] 2xl:w-[calc(100vw_-_195px)]'
      }  p-2 backdrop-blur-xl dark:bg-[#0b14374d] md:right-[30px] md:top-4 xl:top-[20px]`}
    >
      <div className="ml-[6px]">
        <LogOutConfirmModel
          isOpen={isLogoutModalOpen}
          onClose={() => setLogoutModalOpen(false)}
          onConfirm={confirmLogout}
        />
        <div className="h-6 w-[324px] pt-1">
          <span
            className="text-sm font-normal text-navy-700 dark:text-white dark:hover:text-white"
            href=" "
          >
            Pages
            <span className="mx-1 text-sm text-navy-700 hover:text-navy-700 dark:text-white">
              {' '}
              /{' '}
            </span>
          </span>
          <span
            className="text-sm font-normal capitalize text-navy-700  dark:text-white dark:hover:text-white"
            to="#"
          >
            {brandText}
          </span>
        </div>
        <p className="shrink text-[33px] capitalize text-navy-700 dark:text-white">
          <span className="font-bold capitalize hover:text-navy-700 dark:hover:text-white">
            {brandText}
          </span>
        </p>
        {userRole === 'superadmin' && (
          <span className="cursor-pointer text-sm text-gray-600 dark:text-white">
            <SelcetBranch />
          </span>
        )}
      </div>
      <SelectBranchModal
        isOpen={isBranchModalOpen}
        onClose={() => setBranchModalOpen(false)}
      />

      <div className="relative mt-[3px] flex h-[60px] w-[355px] flex-grow items-center justify-around gap-2 rounded-full bg-white px-2 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none md:w-[395px] md:flex-grow-0 md:gap-1 xl:w-[400px] xl:gap-2">
        <div className="flex h-full items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white xl:w-[225px]">
          <p className="pl-3 pr-2 text-xl">
            <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
          </p>
          <input
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
            className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
          />
        </div>

        {searchResults.length > 0 ? (
          <div className="absolute left-0 top-full z-10 mt-2 w-full rounded-md bg-white text-gray-500 shadow-lg dark:bg-navy-700 dark:text-gray-400">
            {searchResults.map((result, index) => (
              <div
                key={index}
                className="cursor-pointer px-4 py-2 hover:bg-gray-100 dark:hover:bg-navy-700"
                onClick={() => handleSelectResult(result)}
              >
                {result.name}
              </div>
            ))}
          </div>
        ) : searchInput ? (
          <div className="absolute left-0 top-full z-10 mt-2 w-full rounded-md bg-white shadow-lg dark:bg-navy-800">
            <div className="px-4 py-2 text-gray-500 dark:text-gray-400">
              No results found
            </div>
          </div>
        ) : null}
        <span
          className="flex cursor-pointer text-xl text-gray-600 dark:text-white lg:hidden "
          onClick={onOpenSidenav}
        >
          <FiAlignJustify className="h-5 w-5" />
        </span>
        {/* start Notification */}
        <Dropdown
          button={
            <div className="relative cursor-pointer">
              <IoMdNotificationsOutline className="h-4 w-4 text-gray-600 dark:text-white" />
              {unreadCount > 0 && (
                <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                  {unreadCount}
                </span>
              )}
            </div>
          }
          animation="origin-[65%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
          children={
            <div className="flex w-[360px] flex-col gap-3 rounded-[20px] bg-white p-4 shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none sm:w-[460px]">
              {notifications && notifications.length === 0 ? (
                <>
                  {' '}
                  <span>No Notifiaction Yet</span>
                </>
              ) : (
                <>
                  {' '}
                  <div className="flex items-center justify-between">
                    <p className="text-base font-bold text-navy-700 dark:text-white">
                      Notifications
                    </p>
                    <button
                      onClick={markAllAsRead}
                      className="text-sm font-bold text-navy-700 hover:text-brand-500 dark:text-white"
                    >
                      Mark all read
                    </button>
                  </div>
                  {notifications.map((notification) => (
                    <div
                      key={notification._id || Date.now()}
                      className="flex w-full items-center"
                      onClick={() => handleNotificationClick(notification)}
                      // onClick={() => markAsRead(notification._id)}
                    >
                      <div className="flex h-full w-[85px] items-center justify-center rounded-xl bg-gradient-to-b from-brand-400 to-brand-500 py-4 text-2xl text-white">
                        <IoMdNotificationsOutline />
                      </div>
                      <div className="ml-2 flex h-full w-full flex-col justify-center rounded-lg px-1 text-sm">
                        <p className="mb-1 text-left text-base font-bold text-gray-900 dark:text-white">
                          {notification.type || 'New Job'}
                        </p>
                        <p className="font-base text-left text-xs text-gray-900 dark:text-white">
                          {notification.message}
                        </p>
                        <div className="flex justify-between">
                          {' '}
                          <p className="mt-1 text-xs text-gray-500">
                            {notification.isRead ? 'Read' : 'Unread'}
                          </p>
                          <p className="mt-1 text-xs text-gray-600">
                            {formatDate(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          }
          classNames={'py-2 top-4 -left-[230px] md:-left-[440px] w-max'}
        />
        {/* start Horizon PRO */}

        <div
          className="cursor-pointer text-gray-600"
          onClick={() => {
            if (darkmode) {
              document.body.classList.remove('dark');
              setDarkmode(false);
            } else {
              document.body.classList.add('dark');
              setDarkmode(true);
            }
          }}
        >
          {darkmode ? (
            <RiSunFill className="h-4 w-4 text-gray-600 dark:text-white" />
          ) : (
            <RiMoonFill className="h-4 w-4 text-gray-600 dark:text-white" />
          )}
        </div>
        <Configurator
          mini={props.mini}
          setMini={props.setMini}
          theme={props.theme}
          setTheme={props.setTheme}
          darkmode={darkmode}
          setDarkmode={setDarkmode}
        />
        <div
          className="cursor-pointer text-gray-600"
          onClick={() => {
            if (darkmode) {
              document.body.classList.remove('dark');
              setDarkmode(false);
            } else {
              document.body.classList.add('dark');
              setDarkmode(true);
            }
          }}
        ></div>
        {/* Profile & Dropdown */}
        <Dropdown
          button={
            state.user.role === 'student' ? (
              <ProfilePic />
            ) : (
              <img
                className="h-10 w-10 cursor-pointer rounded-full"
                src={avatar}
                alt="Elon Musk"
              />
            )
          }
          children={
            <div className="flex h-max w-56 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat pb-4 shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
              <div className="ml-4 mt-3">
                <div className="flex items-center gap-2">
                  {isLoading ? (
                    <p>Loading...</p>
                  ) : (
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                      👋 Hey, {state?.user?.name || 'Guest'}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 h-px w-full bg-gray-200 dark:bg-white/20 " />

              <div className="ml-4 mt-3 flex flex-col">
                {/* <a
                  href=" "
                  onClick={() => navigate('/admin/profile')}
                  className="text-sm text-gray-800 dark:text-white hover:dark:text-white"
                >
                  Profile Settings
                </a> */}

                {/* <Link
                  to={profilePath}
                  className="text-sm text-gray-800 dark:text-white hover:dark:text-white"
                >
                  Profile Settings
                </Link> */}

                <Link
                  href=""
                  onClick={handleLogout}
                  className="mt-3 text-sm font-medium text-red-500 hover:text-red-500"
                >
                  Log Out
                </Link>
              </div>
            </div>
          }
          classNames={'py-2 top-8 -left-[180px] w-max'}
        />
      </div>
    </nav>
  );
};

export default Navbar;
